import { isApplicationSourceRevoked } from "@client/offline/artifactQueryPersistence";
import { APPLICATION_AVAILABILITY_QUERY_OPTIONS } from "@client/components/applications/applicationAvailability";
import { trpc } from "@client/trpc";
import { Button, Skeleton } from "@sfc/ui";
import { Link } from "wouter";
import { HomeSection } from "./HomeSection";

const emptyApplications = (
  <HomeSection title="Your Applications">
    <p className="body-md text-muted-foreground">
      You haven’t started an application yet.
    </p>
    <Link
      href="/apply"
      className="body-md text-primary underline underline-offset-4"
    >
      Explore funding rounds →
    </Link>
  </HomeSection>
);

export function HomeApplications({
  viewerId,
  enabled,
  showEmpty = false,
}: {
  viewerId: string;
  enabled: boolean;
  showEmpty?: boolean;
}) {
  if (!enabled)
    return showEmpty ? (
      <HomeSection title="Your workspace">
        <p className="body-md text-muted-foreground">
          Your funding rounds will appear here when you join one.
        </p>
      </HomeSection>
    ) : null;
  return (
    <ApplicationList key={viewerId} viewerId={viewerId} showEmpty={showEmpty} />
  );
}

function ApplicationList({
  viewerId,
  showEmpty,
}: {
  viewerId: string;
  showEmpty: boolean;
}) {
  const query = trpc.getMyApplications.useQuery(undefined, {
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    gcTime: 0,
  });
  const applications =
    !isApplicationSourceRevoked(query.error) &&
    query.data?.viewerId === viewerId
      ? query.data.applications
      : undefined;
  if (applications?.length === 0) return showEmpty ? emptyApplications : null;
  return (
    <HomeSection title="Your Applications">
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
        {query.isError && applications === undefined ? (
          <div role="alert" className="p-6">
            <p>Couldn’t load your applications.</p>
            <Button variant="nav" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : applications === undefined ? (
          <Skeleton
            className="h-28 w-full"
            role="status"
            aria-label="Loading applications"
          />
        ) : (
          applications.map((application) => (
            <article
              key={application.id}
              className="flex flex-wrap items-center gap-4 p-5"
            >
              <div className="min-w-0 flex-1 basis-64">
                <h3 className="heading-sm [overflow-wrap:anywhere]">
                  {application.roundName}
                </h3>
                <p className="mt-2 body-sm text-muted-foreground">
                  Team:{" "}
                  <Link
                    className="underline underline-offset-4"
                    href={`/teams/${application.team.id}`}
                  >
                    {application.team.name}
                  </Link>
                </p>
              </div>
              <span
                className={
                  application.status === "SUBMITTED"
                    ? "body-sm font-medium text-success"
                    : "body-sm font-medium text-muted-foreground"
                }
              >
                {application.status === "SUBMITTED" ? "Submitted" : "Draft"}
              </span>
              {
                <Button
                  variant="nav"
                  className="text-primary"
                  nativeButton={false}
                  render={
                    <Link
                      href={`/applications/${encodeURIComponent(application.id)}`}
                    />
                  }
                >
                  {application.status === "SUBMITTED" || !application.canOpen
                    ? "View application →"
                    : "Continue application →"}
                </Button>
              }
            </article>
          ))
        )}
      </div>
    </HomeSection>
  );
}
